import React, { useState, useEffect, useContext, useRef } from 'react';
import { Box, Typography, Card, Chip, Button, Dialog, DialogContent, DialogActions, DialogTitle } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FormsBottomNavbar from './../Post Module/FormsBottomNavbar';
import CustomSearchBar from "./../Lease Module/CustomSearchBar";
import { BASE_URL } from './../../Api/ApiUrls';
import { AuthContext } from '../../AuthContext/AuthContext';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const workerTypes = [
  "Painting", "Carpenter", "Flooring", "AC Technician", "Cleaning maid",
  "Gardener", "Sofa Cleaning", "Water Purifier", "Kitchen/Toilet Cleaning", "Plumbing", "Electrical"
];

const DEFAULT_CENTER = [17.3850, 78.4867];
const NORMAL_ZOOM = 13;
const SEARCH_ZOOM = 13; 

// Custom Map Component to handle initialization and cleanup
const MapWrapper = ({ center, zoom, vendors, onMarkerClick, searchLocation }) => {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const searchLocationMarkerRef = useRef(null);

  useEffect(() => {
    // Initialize map
    mapRef.current = L.map(mapContainerRef.current, {
      center,
      zoom,
      zoomControl: false, // disable default LEFT zoom
      maxZoom: 20,
    });

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxNativeZoom: 20
    }).addTo(mapRef.current);

    // ✅ RIGHT SIDE zoom (+ / −)
    L.control.zoom({
      position: 'topright',
    }).addTo(mapRef.current);

    // ✅ FORCE map to render controls immediately
    setTimeout(() => {
      mapRef.current.invalidateSize();
    }, 0);

    // Add markers for vendors
    vendors.forEach(vendor => {
      if (vendor.lat && vendor.long) {
        L.marker([parseFloat(vendor.lat), parseFloat(vendor.long)])
          .addTo(mapRef.current)
          .bindPopup(`${vendor.profession}<br>${vendor.name}`)
          .on('click', () => onMarkerClick(vendor));
      }
    });

    // Cleanup
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []); // Initialize only once

  // Update map when search location changes
  useEffect(() => {
    if (mapRef.current && searchLocation) {
      try {
        mapRef.current.setView([searchLocation.lat, searchLocation.lng], SEARCH_ZOOM, {
          animate: true
        });
        
        // Remove existing search marker
        if (searchLocationMarkerRef.current && mapRef.current.hasLayer(searchLocationMarkerRef.current)) {
          mapRef.current.removeLayer(searchLocationMarkerRef.current);
        }
        
        // Add new search marker
    const searchIcon = L.divIcon({
  className: 'search-location-marker',
  html: '',
  iconSize: [0, 0],
  iconAnchor: [0, 0],
});

        searchLocationMarkerRef.current = L.marker([searchLocation.lat, searchLocation.lng], {
          icon: searchIcon
        }).addTo(mapRef.current);
          
      } catch (error) {
        console.error('Error updating map to search location:', error);
      }
    }
  }, [searchLocation]);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.eachLayer(layer => {
        if (layer instanceof L.Marker && layer !== searchLocationMarkerRef.current) {
          mapRef.current.removeLayer(layer);
        }
      });

      // Add new markers
      vendors.forEach(vendor => {
        if (vendor.lat && vendor.long) {
          L.marker([parseFloat(vendor.lat), parseFloat(vendor.long)])
            .addTo(mapRef.current)
            .bindPopup(`${vendor.profession}<br>${vendor.name}`)
            .on('click', () => onMarkerClick(vendor));
        }
      });

      // Fit bounds to show all vendors if no search location
      if (!searchLocation && vendors.length > 0) {
        const coords = vendors
          .filter(v => v.lat && v.long)
          .map(v => [parseFloat(v.lat), parseFloat(v.long)]);
        
        if (coords.length > 0) {
          const bounds = L.latLngBounds(coords);
          mapRef.current.fitBounds(bounds, {
            padding: [50, 50],
            maxZoom: NORMAL_ZOOM,
            animate: true
          });
        }
      }
    }
  }, [vendors, onMarkerClick, searchLocation]);

  return <div ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />;
};

const HomeService = () => { 
  const [vendors, setVendors] = useState([]); 
  const { userId } = useContext(AuthContext);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [searchLocation, setSearchLocation] = useState(null);
  const [mapKey, setMapKey] = useState(0);
  const navigate = useNavigate();

  const { isGuest } = useContext(AuthContext);
const [openGuestModal, setOpenGuestModal] = useState(false);

const handleCardClick = () => {
  if (isGuest) {
    setOpenGuestModal(true); // 👈 show popup
  } else {
    navigate(`/work-details/${selectedWorker.vendor_id}`);
  }
};
const maskMobile = (mobile) => {
  if (!mobile) return "";
  return mobile.slice(0, 5) + "xxxxx"; // 99999xxxxx
};

const maskEmail = (email) => {
  if (!email) return "";
  const [name, domain] = email.split("@");
  return "dummy@" + domain; // dummy@gmail.com
};



  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .leaflet-control-attribution {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Fetch user location and vendors data
  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        const response = await fetch(`${BASE_URL}/users/${userId}/`);
        const userData = await response.json();
        
        if (userData.lat && userData.long) {
          setMapCenter([parseFloat(userData.lat), parseFloat(userData.long)]);
        }
      } catch (error) {
        console.error("Error fetching user location:", error);
      }
    };

    const fetchVendors = async () => {
      try {
        const response = await fetch(`${BASE_URL}/vendors/`);
        const data = await response.json();
        setVendors(data);
        setFilteredVendors(data);
      } catch (error) {
        console.error("Error fetching vendors:", error);
      }
    };

    if (userId) {
      fetchUserLocation();
    }
    fetchVendors();
  }, [userId]);

  // Filter vendors when selectedType changes
  useEffect(() => {
    if (vendors.length > 0) {
      const filtered = selectedType
        ? vendors.filter(vendor => 
            vendor.profession && vendor.profession.toLowerCase().includes(selectedType.toLowerCase()))
        : vendors;
      
      // If search location is selected, sort by distance
      if (searchLocation) {
        const centerLat = searchLocation.lat;
        const centerLng = searchLocation.lng;
        
        const sortedFiltered = filtered
          .map(vendor => {
            if (!vendor.lat || !vendor.long) return vendor;
            const distance = L.latLng([parseFloat(vendor.lat), parseFloat(vendor.long)])
              .distanceTo([centerLat, centerLng]);
            return { ...vendor, distance };
          })
          .sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
        
        setFilteredVendors(sortedFiltered);
      } else {
        setFilteredVendors(filtered);
      }
    }
  }, [selectedType, vendors, searchLocation]);

  const handleTypeSelection = (type) => {
    setSelectedWorker(null);
    setSelectedType(prev => (prev === type ? null : type));
  };

  const handleMarkerClick = (vendor) => {
    setSelectedWorker(vendor);
  };

  const handleSelectedLocation = (locationData) => {
    setSearchLocation(locationData);
  };

  const clearSearch = () => {
    setSearchLocation(null);
    setSelectedType(null);
    setMapKey(prev => prev + 1);
    setSelectedWorker(null);
  };

  const resetToUserLocation = () => {
    clearSearch();
    if (userId) {
      fetch(`${BASE_URL}/users/${userId}/`)
        .then(response => response.json())
        .then(userData => {
          if (userData.lat && userData.long) {
            setMapCenter([parseFloat(userData.lat), parseFloat(userData.long)]);
          }
        })
        .catch(error => console.error("Error fetching user location:", error));
    }
  };

  // Calculate zoom level
  const getZoomLevel = () => {
    return NORMAL_ZOOM; // Always use NORMAL_ZOOM (13)
  };

  // Calculate center
  const getMapCenter = () => {
    if (searchLocation) {
      return [searchLocation.lat, searchLocation.lng];
    }
    return mapCenter;
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        mx: "auto",
        position: 'relative',
        bgcolor: 'rgb(239, 231, 221)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Sticky Search Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          bgcolor: 'rgb(239, 231, 221)',
          px: '2%',
          py: '1%',
          flexShrink: 0
        }}
      >
        <CustomSearchBar 
          onPlaceSelected={handleSelectedLocation}
          onClear={clearSearch}
        />
      </Box>

      {/* Search Controls */}
      {/* {searchLocation && (
        <Box sx={{ 
          px: '4%',
          pt: '1%',
          flexShrink: 0,
          display: 'flex',
          gap: 1,
          alignItems: 'center'
        }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Showing workers near: {searchLocation.address}
          </Typography>
          <Button 
            size="small" 
            variant="outlined" 
            onClick={resetToUserLocation}
            sx={{ ml: 'auto', fontSize: '0.75rem' }}
          >
            Reset to My Location
          </Button>
        </Box>
      )} */}

      {/* Worker Type Chips */}
      <Box sx={{ 
        px: '4%',
        pt: '1%',
        pb: '1%',
        flexShrink: 0
      }}>
        <Typography variant="subtitle1" sx={{ mb: '0.5rem' }}>
          Looking for Home Services
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: '0.5rem',
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            pb: '0.5rem'
          }}
        >
          {workerTypes.map((type, index) => (
            <Chip
              key={index}
              label={type}
              variant="filled"
              onClick={() => handleTypeSelection(type)}
              sx={{
                flexShrink: 0,
                bgcolor: selectedType === type ? '#000000' : 'transparent',
                color: selectedType === type ? '#ffffff' : '#000000',
                border: '0.0625rem solid black',
                fontWeight: 'bold',
                '&:hover': {
                bgcolor: selectedType === type ? '#000000' : 'transparent',
                color: selectedType === type ? '#ffffff' : '#000000',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* No vendors message - Only shown when no vendors */}
      {selectedType && filteredVendors.length === 0 && (
        <Box sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          px: '4%',
          textAlign: 'center',
          flexShrink: 0
        }}>
          <Box sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '4%',
            borderRadius: '0.5rem',
            textAlign: 'center',
            width: '90%',
            maxWidth: '500px'
          }}>
            <Typography variant="h6">
              No workers available in this category
            </Typography>
            <Typography variant="body2" sx={{ mt: '0.5rem' }}>
              {searchLocation 
                ? `We couldn't find any "${selectedType}" workers near ${searchLocation.address}`
                : `We couldn't find any workers matching "${selectedType}"`
              }
            </Typography>
            <Button 
              variant="outlined" 
              sx={{ mt: 2 }}
              onClick={clearSearch}
            >
              Clear Search
            </Button>
          </Box>
        </Box>
      )}
      
      {/* Map Container - Takes remaining space above the bottom navbar */}
      {filteredVendors.length > 0 && (
        <Box sx={{ 
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          pb: '60px' // Space for bottom navbar
        }}>
          <Box sx={{ 
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0
          }}>
            <MapWrapper 
              key={`map-${mapKey}`}
              center={getMapCenter()}
              zoom={getZoomLevel()}
              vendors={filteredVendors}
              onMarkerClick={handleMarkerClick}
              searchLocation={searchLocation}
            />
          </Box>

          {/* Floating Worker Card - Positioned above bottom navbar */}
          {selectedWorker && (
            <Box
              sx={{
                position: 'absolute',
                bottom: '80px', // Position above the bottom navbar
                left: 0,
                right: 0,
                px: '4%',
                zIndex: 999,
              }}
            >
              <Card
               onClick={handleCardClick}
                sx={{
                  borderRadius: '1rem',
                  p: '4%',
                  backgroundColor: '#fff',
                  boxShadow: 3,
                  width: '93%'
                }}
              >
                <Box sx={{ display: 'flex', gap: '4%' }}>
                  <img
                    src={`${BASE_URL}${selectedWorker.profile}`}
                    alt={selectedWorker.name}
                    style={{
                      width: '25%',
                      maxWidth: '100px',
                      height: 'auto',
                      aspectRatio: '1/1',
                      borderRadius: '10%',
                      objectFit: 'cover',
                    }}
                  />
                  <Box sx={{ width: '70%' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {selectedWorker.profession}
                    </Typography>
                    <Typography variant="body2">Name: {selectedWorker.name}</Typography>
                   <Typography variant="body2">
  Mobile: {isGuest ? maskMobile(selectedWorker.mobile) : selectedWorker.mobile}
</Typography>

<Typography variant="body2">
  Email: {isGuest ? maskEmail(selectedWorker.email) : selectedWorker.email}
</Typography>

                    <Typography variant="body2">Experience: {selectedWorker.experience}+ year(s)</Typography>
                    <Typography variant="body2">⭐⭐⭐⭐</Typography>
                    {searchLocation && selectedWorker.distance && (
                      <Typography variant="body2" sx={{ color: 'green', fontWeight: 'bold' }}>
                        {Math.round(selectedWorker.distance / 1000)} km away
                      </Typography>
                    )}
                    {isGuest && (
  <Typography variant="caption" color="error">
    Login to view full contact details
  </Typography>
)}

                  </Box>
                </Box>
              </Card>
            </Box>
          )}
        </Box>
      )}

      <Dialog open={openGuestModal} onClose={() => setOpenGuestModal(false)}>
  <DialogTitle>Login Required</DialogTitle>

  <DialogContent>
    <Typography>
      Please Register and login to access this feature.
    </Typography>
  </DialogContent>

  <DialogActions>
    <Button onClick={() => setOpenGuestModal(false)}>
      Cancel
    </Button>

    <Button
      variant="contained"
      onClick={() => navigate('/')}
    >
      Login
    </Button>
  </DialogActions>
</Dialog>

      
      {/* Bottom Navigation - Fixed at bottom */}
      <Box sx={{ 
        position: 'absolute', 
        bottom: 0, 
        left: 0, 
        right: 0,
        width: '100%',
        zIndex: 1000
      }}>
        <FormsBottomNavbar /> 
      </Box>
    </Box>
  );
};

export default HomeService;